/**
 * /grocery/:cardId — the fridge card page.
 *
 * Asks the SERVER what the card is (never infers from the URL, the
 * VehicleRouteGuard pattern), then shows either the claim flow (unclaimed) or
 * the list (claimed, no login). The session is remembered per card so the next
 * tap opens the list directly.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, ShoppingBasket, Users, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Toaster } from '../../components/ui/sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import GroceryListEngine from '../../components/grocery/GroceryListEngine';
import GroceryMembersPanel from '../../components/grocery/GroceryMembersPanel';
import GroceryModeToggle from '../../components/grocery/GroceryModeToggle';
import { useGuestTitle } from '../../lib/guestRoute';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
export const storeKey = (cardId) => `yulontap_grocery:${cardId}`;

export default function GroceryCardPage() {
    const { cardId } = useParams();
    const [params] = useSearchParams();
    const { t, i18n } = useTranslation();
    const [state, setState] = useState(null);
    const [session, setSession] = useState(null);
    const [showMembers, setShowMembers] = useState(false);
    const [pin, setPin] = useState('');
    const [form, setForm] = useState({
        language: (i18n.language || 'en').slice(0, 2), first_name: '', phone: '', email: '',
        address_street: '', address_apartment: '', address_postal_code: '', address_city: '',
        pin: '', consent_transactional: false, consent_promotional: false,
    });
    const [saving, setSaving] = useState(false);

    // A fridge card is a GUEST page: the tab carries the store's name, never the portal's.
    useGuestTitle(state?.organization_name);

    const loadState = useCallback(async () => {
        const r = await fetch(`${API}/api/grocery/card/${cardId}`);
        const j = await r.json();
        setState(r.ok ? j : { error: j.detail || 'error' });
        return r.ok ? j : null;
    }, [cardId]);

    useEffect(() => {
        (async () => {
            const s = await loadState();
            let saved = null;
            try { saved = JSON.parse(localStorage.getItem(storeKey(cardId)) || 'null'); } catch (_e) { /* ignore */ }
            if (saved?.session) {
                const probe = await fetch(`${API}/api/grocery/list?lang=en`,
                    { headers: { 'X-Grocery-Session': saved.session } });
                if (probe.ok) { setSession(saved.session); return; }
            }
            if (s?.claimed && !s.household?.open_pin_required) {
                const r = await fetch(`${API}/api/grocery/open`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ card_id: cardId, pin: null }),
                });
                if (r.ok) {
                    const j = await r.json();
                    setSession(j.session);
                    try { localStorage.setItem(storeKey(cardId), JSON.stringify({ session: j.session })); } catch (_e) { /* ignore */ }
                }
            }
        })();
    }, [cardId, loadState]);

    const openWithPin = async () => {
        const r = await fetch(`${API}/api/grocery/open`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ card_id: cardId, pin }),
        });
        if (!r.ok) { toast.error(t('grocery.pinInvalid')); return; }
        const j = await r.json();
        setSession(j.session);
        try { localStorage.setItem(storeKey(cardId), JSON.stringify({ session: j.session })); } catch (_e) { /* ignore */ }
    };

    const claim = async () => {
        if (!form.first_name.trim()) { toast.error(t('grocery.claim.needName')); return; }
        if (!form.phone && !form.email) { toast.error(t('grocery.claim.needContact')); return; }
        if (!form.address_street || !form.address_postal_code || !form.address_city) {
            toast.error(t('grocery.claim.needAddress')); return;
        }
        if (!form.consent_transactional) { toast.error(t('grocery.claim.needConsent')); return; }
        setSaving(true);
        try {
            const r = await fetch(`${API}/api/grocery/claim`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, ...form }),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || 'claim_failed');
            setSession(j.session);
            try { localStorage.setItem(storeKey(cardId), JSON.stringify({ session: j.session })); } catch (_e) { /* ignore */ }
            await loadState();
            toast.success(j.in_service_area ? t('grocery.claim.done') : t('grocery.outsideServiceArea'));
        } catch (e) {
            toast.error(t('grocery.claim.failed'));
        } finally { setSaving(false); }
    };

    const deleteHousehold = async () => {
        if (!window.confirm(t('grocery.deleteConfirm'))) return;
        const r = await fetch(`${API}/api/grocery/household`, {
            method: 'DELETE', headers: { 'X-Grocery-Session': session } });
        if (!r.ok) { toast.error(t('grocery.deleteFailed')); return; }
        try { localStorage.removeItem(storeKey(cardId)); } catch (_e) { /* ignore */ }
        setSession(null); await loadState();
        toast.success(t('grocery.deleted'));
    };

    if (!state) {
        return <div className="min-h-screen flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-emerald-500" /></div>;
    }
    if (state.error) {
        return <div className="min-h-screen flex items-center justify-center p-6 text-center">
            <p className="text-slate-700 dark:text-slate-200" data-testid="grocery-card-error">{t('grocery.cardNotFound')}</p></div>;
    }

    // ── claimed + session: the list ──
    if (session) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-6 pb-44 sm:px-6 lg:px-10">
                {/* A fridge card is a guest surface with no portal chrome, so it
                    mounts its own toaster — otherwise every cheer is swallowed. */}
                {/* TOASTS GO TO THE BOTTOM. At the top they sat exactly on the
                    item sheet's own header: a cheer ("saved for next time")
                    covered the HEART and the CLOSE button, so a tap went to the
                    toast instead of the control a finger was aiming at. The
                    just-added bar keeps its own place at the top (top-[5.5rem]). */}
                <Toaster position="bottom-center" offset={96} data-testid="grocery-toaster" />
                <div className="mx-auto max-w-3xl">
                    <GroceryListEngine session={session} onMembers={() => setShowMembers(true)} />
                    {showMembers && (
                        <GroceryMembersPanel session={session} onClose={() => setShowMembers(false)} />
                    )}
                    <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800">
                        <Button variant="ghost" onClick={deleteHousehold}
                            className="min-h-[44px] gap-2 text-rose-700 dark:text-rose-300" data-testid="grocery-delete-household">
                            <Trash2 className="w-4 h-4" />{t('grocery.deleteHousehold')}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">{t('grocery.privacyNote')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // ── claimed + PIN required ──
    if (state.claimed) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="w-full max-w-sm space-y-4" data-testid="grocery-pin-gate">
                    <div className="flex justify-end">
                        <GroceryModeToggle memberKey="fridge" testId="grocery-mode-toggle-pin" compact />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('grocery.enterPin')}
                    </h1>
                    <Input value={pin} onChange={(e) => setPin(e.target.value)} inputMode="numeric"
                        maxLength={4} className="min-h-[44px] text-center text-2xl tracking-widest"
                        data-testid="grocery-pin-input" />
                    <Button onClick={openWithPin} className="w-full min-h-[44px]" data-testid="grocery-pin-submit">
                        {t('grocery.openList')}
                    </Button>
                </div>
            </div>
        );
    }

    // ── unclaimed: the claim flow ──
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-8 pb-32 sm:px-6">
            <div className="mx-auto max-w-lg space-y-5" data-testid="grocery-claim-form">
                <div className="flex items-center gap-3">
                    <ShoppingBasket className="w-8 h-8 text-emerald-500" strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-foreground"
                            style={{ fontFamily: 'Manrope, sans-serif' }}>{t('grocery.claim.title')}</h1>
                        <p className="text-sm text-muted-foreground">{t('grocery.claim.subtitle')}</p>
                    </div>
                    <GroceryModeToggle memberKey="fridge" testId="grocery-mode-toggle-claim" compact />
                </div>

                <div>
                    <Label className="mb-2 block">{t('grocery.claim.language')}</Label>
                    <Select value={form.language} onValueChange={set('language')}>
                        <SelectTrigger className="min-h-[44px]" data-testid="grocery-claim-language">
                            <SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="fr">Français</SelectItem>
                            <SelectItem value="es">Español</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {[['first_name', 'firstName', 'text'], ['phone', 'mobile', 'tel'], ['email', 'email', 'email'],
                  ['address_street', 'street', 'text'], ['address_apartment', 'apartment', 'text'],
                  ['address_postal_code', 'postalCode', 'text'], ['address_city', 'city', 'text'],
                  ['pin', 'optionalPin', 'text']]
                  // A DEMO organization never asks anyone to invent a PIN: in a
                  // showroom that is friction with no point, and the server
                  // ignores one anyway.
                  .filter(([key]) => !(key === 'pin' && state.is_demo))
                  .map(([key, label, type]) => (
                    <div key={key}>
                        <Label className="mb-2 block">{t(`grocery.claim.${label}`)}</Label>
                        <Input type={type} value={form[key]} onChange={set(key)} className="min-h-[44px]"
                            inputMode={key === 'pin' ? 'numeric' : undefined}
                            maxLength={key === 'pin' ? 4 : undefined}
                            data-testid={`grocery-claim-${key.replace(/_/g, '-')}`} />
                    </div>
                ))}

                <label className="flex items-start gap-3 cursor-pointer" data-testid="grocery-consent-transactional">
                    <Checkbox checked={form.consent_transactional}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent_transactional: !!v }))} />
                    <span className="text-sm text-foreground">{t('grocery.claim.consentTransactional')}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="grocery-consent-promotional">
                    <Checkbox checked={form.consent_promotional}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, consent_promotional: !!v }))} />
                    <span className="text-sm text-muted-foreground">{t('grocery.claim.consentPromotional')}</span>
                </label>

                <Button onClick={claim} disabled={saving} className="w-full min-h-[44px] gap-2"
                    data-testid="grocery-claim-submit">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                    {t('grocery.claim.submit')}
                </Button>
                <p className="text-xs text-muted-foreground">{t('grocery.privacyNote')}</p>
            </div>
        </div>
    );
}
